import React from 'react'
import { Card } from 'react-bootstrap'
import type { Blog } from '@ztechapps/common.contract'
import { useList } from '@ztechapps/common.hook'
import { DataSuspense, ShowIf } from '@ztechapps/webui.util'
import { useBlogCategoryList } from '../../BlogCategory/useBlogCategoryList'
import { useBlogListContext } from '../Context'
import { treeBlogQueries } from './graphql'
import { BlogCategoryBlogTable } from './BlogCategoryBlogTable'
import { BlogCategoryTree } from './BlogCategoryTree'
import { BlogCategoryToolbar } from './BlogCategoryToolbar'
import { BlogCategoryTreeViewProvider, useBlogCategoryTreeViewContext } from './Context'
import { WhatsNewNote } from './WhatsNewNote'
import styles from './BlogCategoryTreeView.module.scss'

const Layout = () => {
  const { selectedBlogCategory, isWhatsNewSelected } = useBlogCategoryTreeViewContext()

  return (
    <div className={`${styles.shrinkable} d-flex flex-column flex-md-row gap-3 flex-grow-1`}>
      <div className={`${styles.pane} ${styles.treePane} d-flex flex-column`}>
        <Card className={`${styles.shrinkable} flex-grow-1`}>
          <Card.Header>
            <BlogCategoryToolbar />
          </Card.Header>
          <Card.Body className={`${styles.shrinkable} flex-grow-1 overflow-auto`}>
            <BlogCategoryTree />
          </Card.Body>
        </Card>
      </div>
      <div className={`${styles.pane} d-flex flex-column flex-grow-1`}>
        <Card className={`${styles.shrinkable} flex-grow-1`}>
          <ShowIf condition={!!selectedBlogCategory}>
            <Card.Header className="fw-semibold">{selectedBlogCategory?.name}</Card.Header>
          </ShowIf>
          <ShowIf condition={isWhatsNewSelected}>
            <WhatsNewNote />
          </ShowIf>
          <Card.Body className={`${styles.shrinkable} flex-grow-1 overflow-auto px-0 pt-0 pb-5`}>
            <BlogCategoryBlogTable />
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

export type BlogCategoryTreeViewProps = {
  /** The blog categories carry no tenant id (Payroll), so the reserved Uncategorized / What's New ids are matched in their tenant-free form. */
  tenantFree?: boolean
}

export const BlogCategoryTreeView = ({ tenantFree }: BlogCategoryTreeViewProps) => {
  // The blog table and the counts follow the Blog list's toolbar search, filter and sort; the category list and the
  // delete rule's blog list never do.
  const { list: blogs } = useBlogListContext()
  const categoryList = useBlogCategoryList()
  const blogList = useList<Blog>({ ...treeBlogQueries.allConfig, limit: 0, sort: { updatedAt: -1 } })
  // Loading shows only until the first records arrive: a refetch after a filter or a move keeps the tree, and its
  // selection, mounted.
  const loading = (categoryList.loading && !categoryList.list) || (blogList.loading && !blogList.list)

  return (
    <DataSuspense loading={loading} error={categoryList.error ?? blogList.error}>
      <BlogCategoryTreeViewProvider
        categories={categoryList.list}
        blogs={blogs as Blog[]}
        allBlogs={blogList.list}
        tenantFree={tenantFree}
      >
        <Layout />
      </BlogCategoryTreeViewProvider>
    </DataSuspense>
  )
}
